<script lang="ts">
  import { setContext } from 'svelte';

  import { Title, Card, OverviewTable, Icon, Button } from '$shared/components';
  import Label from '$shared/constants/Label';
  import type { OverviewSectionOption } from '$shared/models/OverviewSectionOption';
  import { GlobalAppContextKey } from '$shared/constants/GlobalAppContextKey';
  import type { Readable } from 'svelte/store';

  interface Props {
    loading?: boolean;
    hasAction?: boolean;
    contextStore: Readable<{ readonly: boolean }> | null;
    option: OverviewSectionOption;
    customRowComponent?: ConstructorOfATypedSvelteComponent | undefined;
    actions?: import('svelte').Snippet;
    children?: import('svelte').Snippet;
    onadd?: () => void;
    onclick?: (payload: any) => void;
    onclickoption?: (payload: { option: CommonList; row: any }) => void;
    onclickAlert?: (payload: any) => void;
    onevent?: (payload: any) => void;
  }

  let {
    loading = false,
    hasAction = false,
    contextStore,
    option,
    customRowComponent = undefined,
    actions,
    children,
    onadd,
    onclick,
    onclickoption,
    onclickAlert,
    onevent
  }: Props = $props();

  const onClickAdd = (): void => {
    onadd?.();
  };

  setContext<Readable<{ readonly: boolean }>>(
    GlobalAppContextKey.OverviewSectionContextStore,
    contextStore
  );

  let getRowOptionsWithReadonly = $derived((row: any) => {
    const options = option.getRowOptions(row) || [];
    if ($contextStore?.readonly) {
      options.forEach((opt: any) => (opt.disabled = true));
    }
    return options;
  });
</script>

{#if option}
  <div data-cy={option.title} class="overview-section">
    <Card>
      <div class="header">
        <div class="title">
          <Title type="h3">{option.title}</Title>
        </div>
        {#if !loading}
          <div class="actions">
            {#if !option.hasRows && option.actionLabel}
              <div class="action-label">
                <div class="icon" class:show={option.actionLabelIcon}>
                  <Icon value={option.actionLabelIcon} />
                </div>
                <div class="text">{option.actionLabel}</div>
              </div>
            {/if}
            {#if hasAction}
              <Button
                name={option.title}
                disabled={$contextStore?.readonly}
                icon="icon-plus"
                onclick={onClickAdd}
              >
                {Label.Add}
              </Button>
            {/if}
            {@render actions?.()}
          </div>
        {/if}
      </div>
      {#if option.hasTable && (loading || customRowComponent || option.hasRows)}
        <div class="table">
          <OverviewTable
            {loading}
            name={option.title}
            columns={option.columns}
            rows={option.rows}
            alerts={option.alerts}
            getRowOptions={getRowOptionsWithReadonly}
            {customRowComponent}
            {onclick}
            {onclickAlert}
            {onclickoption}
            {onevent}
          />
        </div>
      {/if}
      {@render children?.()}
    </Card>
  </div>
{:else}
  <div class="invalid">Invalid Option</div>
{/if}

<style>
  .overview-section {
    margin-bottom: 16px;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .table {
    margin-top: 16px;
  }
  .actions {
    display: flex;
    align-items: center;
  }
  .action-label {
    display: inline-flex;
    align-items: center;
  }
  .action-label .icon {
    margin-right: 8px;
    display: none;
    align-items: center;
  }
  .action-label .icon.show {
    display: inline-flex;
  }
  .action-label .text {
    font-style: italic;
    font-weight: normal;
    font-size: 11px;
    line-height: 18px;
    color: var(--grey-medium);
    margin-right: 8px;
  }
</style>
